import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';

const FriendsDropdown = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [onlineFriends, setOnlineFriends] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadFriendsData();

      // Set up realtime subscription for friend requests
      const requestsSubscription = supabase
        .channel('friend_requests_header')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'friend_requests',
            filter: `to_user_id=eq.${user.id}`
          },
          () => {
            loadFriendsData();
          }
        )
        .subscribe();

      return () => {
        requestsSubscription.unsubscribe();
      };
    }
  }, [user]);

  const loadFriendsData = async () => {
    try {
      setLoading(true);

      // Get pending requests count
      const { data: requests, error: requestsError } = await supabase
        .from('friend_requests')
        .select('id')
        .eq('to_user_id', user.id)
        .eq('status', 'pending');

      if (!requestsError) {
        setPendingCount(requests?.length || 0);
      }

      // Get online/playing friends
      const { data: friendships } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id);

      if (friendships && friendships.length > 0) {
        const friendIds = friendships.map(f => f.friend_id);

        // Get friend profiles with presence
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, email, full_name, avatar_url')
          .in('id', friendIds);

        const { data: presenceData } = await supabase
          .from('user_presence')
          .select(`
            user_id,
            status,
            game_id,
            custom_status,
            game:game_id (
              id,
              title
            )
          `)
          .in('user_id', friendIds)
          .in('status', ['online', 'playing']);

        if (profiles && presenceData) {
          const presenceMap = {};
          presenceData.forEach(p => {
            presenceMap[p.user_id] = {
              status: p.status,
              gameId: p.game_id,
              gameName: p.game?.title || null,
              customStatus: p.custom_status
            };
          });

          const onlineFriendsList = profiles
            .filter(profile => presenceMap[profile.id])
            .map(profile => ({
              id: profile.id,
              fullName: profile.full_name,
              avatarUrl: profile.avatar_url,
              presence: presenceMap[profile.id]
            }))
            .slice(0, 5); // Show max 5

          setOnlineFriends(onlineFriendsList);
        }
      }

    } catch (err) {
      console.error('Error loading friends data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    return status === 'playing' ? 'bg-blue-500' : 'bg-green-500';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-muted/50 transition-colors"
        title="Friends"
      >
        <Icon name="Users" size={20} className="text-foreground" />
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
            {pendingCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50">
            {/* Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Users" size={18} />
                  Friends
                </h3>
                <Link
                  to="/user-profile-settings?tab=friends"
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-primary hover:underline"
                >
                  View All
                </Link>
              </div>
            </div>

            {/* Pending Requests */}
            {pendingCount > 0 && (
              <Link
                to="/user-profile-settings?tab=friends"
                onClick={() => setIsOpen(false)}
                className="block p-4 border-b border-border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
                      <Icon name="UserPlus" size={20} className="text-red-500" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Friend Requests</p>
                      <p className="text-sm text-muted-foreground">{pendingCount} pending</p>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                </div>
              </Link>
            )}

            {/* Online Friends */}
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto" />
              </div>
            ) : onlineFriends.length > 0 ? (
              <div className="max-h-80 overflow-y-auto">
                <div className="px-4 py-2 bg-muted/30">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Online Now
                  </p>
                </div>
                {onlineFriends.map(friend => (
                  <Link
                    key={friend.id}
                    to={`/user-profile-settings?tab=friends`}
                    onClick={() => setIsOpen(false)}
                    className="block p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <img
                          src={friend.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(friend.fullName)}&background=6366f1&color=fff`}
                          alt={friend.fullName}
                          className="w-10 h-10 rounded-full"
                        />
                        <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-card ${getStatusColor(friend.presence.status)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{friend.fullName}</p>
                        {friend.presence.status === 'playing' && friend.presence.gameName ? (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Icon name="Gamepad2" size={12} />
                            <span className="truncate">{friend.presence.gameName}</span>
                          </div>
                        ) : (
                          <p className="text-sm text-green-500">Online</p>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted-foreground mb-2">No friends online</p>
                <Link
                  to="/user-profile-settings?tab=friends"
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-primary hover:underline"
                >
                  Find friends
                </Link>
              </div>
            )}

            {/* Footer */}
            <div className="p-3 border-t border-border">
              <Link
                to="/user-profile-settings?tab=friends"
                onClick={() => setIsOpen(false)}
                className="block w-full px-4 py-2 text-center text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors font-medium"
              >
                Manage Friends
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FriendsDropdown;
